import axios from 'axios';

// En desarrollo local (pnpm dev) apuntamos al puerto 5057 directamente.
// En Docker (Nginx), usamos rutas relativas para aprovechar el proxy de Nginx.
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const rawApiUrl = import.meta.env.VITE_API_URL || (isLocal ? 'http://localhost:5057' : '');
const API_BASE = rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Si hay token en storage, enviarlo como Bearer en cada request.
api.interceptors.request.use((requestConfig) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    requestConfig.headers = requestConfig.headers || {};
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      localStorage.removeItem('accessToken');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const subastaService = {
  // Obtener todas las subastas con filtros
  getAll: (filters = {}) =>
    api.get('/subastas', { params: filters }),

  // Conteo por valor de cada filtro (facetas), respetando los demás filtros
  getFacets: (filters = {}) =>
    api.get('/subastas/facets', { params: filters }),

  // Estadísticas por fase del pipeline (conteo + tiempo medio de proceso)
  getFasesStats: () => api.get('/subastas/fases-stats'),

  // Obtener una subasta por ID
  getById: (id) => api.get(`/subastas/${id}`),

  // Guardar los enlaces de trabajo del grupo sobre una subasta
  updateLinks: (id, links) => api.patch(`/subastas/${id}/links`, { links }),

  // Marcar/desmarcar favorito manual del grupo (prevalece sobre la IA)
  updateInteresante: (id, interesante) => api.patch(`/subastas/${id}/interesante`, { interesante }),

  // Obtener historial de chat de una subasta
  getChatHistory: (id) => api.get(`/subastas/${id}/chat`),

  // Enviar una pregunta al chat RAG de una subasta
  chatWithAi: (id, data) => api.post(`/subastas/${id}/chat`, data),

  // Forzar reindexado del expediente para RAG
  reindexChat: (id) => api.post(`/subastas/${id}/chat/reindex`),

  // Obtener estadísticas
  getStats: () => api.get('/subastas/stats'),

  // Obtener solo subastas interesantes
  getInteresantes: () =>
    api.get('/subastas/filtro/interesantes'),

  // Crear una subasta (testing)
  create: (data) => api.post('/subastas', data),

  // Actualizar una subasta
  update: (id, data) =>
    api.put(`/subastas/${id}`, data),

  // Eliminar una subasta
  delete: (id) => api.delete(`/subastas/${id}`),
};

export const remanenteService = {
  // Listado con filtros y paginación
  getAll: (filters = {}) => api.get('/remanentes', { params: filters }),

  // Conteo por valor de cada filtro (facetas)
  getFacets: (filters = {}) => api.get('/remanentes/facets', { params: filters }),

  // Resumen agrupado por beneficiario
  getBeneficiariosStats: (filters = {}) => api.get('/remanentes/beneficiarios-stats', { params: filters }),

  // Métricas por etiqueta (auto/manual/mixto)
  getEtiquetasStats: (filters = {}) => api.get('/remanentes/etiquetas-stats', { params: filters }),

  // Cola por fase del pipeline de remanentes (Torre de Control)
  getFasesStats: () => api.get('/remanentes/fases-stats'),

  // Desglose por origen (`tipo`) para la tarta de la Torre de Control
  getOrigenesStats: () => api.get('/remanentes/origenes-stats'),

  // Detalle de un remanente (con gestión y subasta enlazada si es sobrante)
  getById: (id) => api.get(`/remanentes/${id}`),

  // Actualizar la provincia del remanente
  updateProvincia: (id, provincia) => api.patch(`/remanentes/${id}/provincia`, { provincia }),

  // Actualizar gestión (estado, notas, etiquetas, links)
  updateGestion: (id, data) => api.patch(`/remanentes/${id}/gestion`, data),

  // Etiquetas usadas por el grupo (desplegables y filtro)
  getEtiquetas: () => api.get('/remanentes/etiquetas'),

  // Autoetiquetar un remanente con IA (usa prompt configurado en setup)
  autoEtiquetar: (id) => api.post(`/remanentes/${id}/autoetiquetar`),

  // Reencolar remanentes para repetir enriquecimiento IA
  reanalizarIA: (payload = {}) => api.post('/remanentes/reanalizar-ia', payload),

  // Emitir una ficha PDF compartible de un remanente. El inventario, la URL
  // pública y la revocación son comunes a todos los orígenes: fichaService.
  crearFichaPdf: (id) => api.post(`/remanentes/${id}/ficha-pdf`),

  // Localizar al beneficiario en TM² (TimeMachine) y guardar la documentación
  buscarTm2: (id) => api.post(`/remanentes/${id}/tm2-buscar`),

  // Igual, pero una sola búsqueda por agrupado de beneficiario (se guarda en todos)
  buscarTm2Beneficiario: (beneficiario) => api.post('/remanentes/tm2-buscar-beneficiario', { beneficiario }),

  // Ejecutar el scraper de remanentes manualmente (admin).
  // Con `anio` recorre ese año completo del BOE, independiente del incremental.
  runScraper: (anio) => api.post('/scheduler/run/remanentes', anio ? { anio } : {}),
};

export const schedulerService = {
  // Obtener estado del scheduler
  getStatus: () => api.get('/scheduler/status'),

  // Ejecutar pipeline completo manualmente
  run: () => api.post('/scheduler/run'),

  // Ejecutar scraping manualmente
  runScraper: () => api.post('/scheduler/run/scraper'),

  // Ejecutar processor IA manualmente
  runProcessor: () => api.post('/scheduler/run/processor'),

  // Ingesta L0/L1 (crawlers nuevos): 'sumario' | 'teju'. Sin rango → incremental;
  // con { desde, hasta } → backfill de ese rango.
  runIngesta: (fuente, rango) => api.post(`/scheduler/run/ingesta/${fuente}`, rango || {}),

  // Estado de las fuentes de ingesta (cursor, cobertura, huecos, contadores)
  getIngestaStatus: () => api.get('/scheduler/ingesta-status'),

  // Libera locks de líder huérfanos de la ingesta (tras un despliegue/crash)
  unlockIngesta: () => api.post('/scheduler/run/ingesta-unlock'),
};

// Sistema común de fichas PDF compartibles, sea cual sea su origen
// (remanentes, edictos TEJU...). Emitir una ficha es cosa de cada origen;
// consultarlas, abrirlas y revocarlas se hace siempre por aquí.
export const fichaService = {
  // Inventario global con su uso (pestaña Fichas de /logs)
  list: () => api.get('/fichas'),

  // URL pública del PDF: es la que se comparte, y cada apertura queda
  // registrada. No requiere sesión — el token ES la credencial.
  pdfUrl: (token) => `${API_BASE}/fichas/${token}/pdf`,

  // Revocar una ficha: su enlace compartido deja de funcionar al instante
  revoke: (fichaId) => api.delete(`/fichas/${fichaId}`),
};

// Explorador de la capa de ingesta L0/L1/L2 (solo lectura, admin+)
export const ingestaService = {
  getResumen: () => api.get('/ingesta/resumen'),
  getL0: (filters = {}) => api.get('/ingesta/l0', { params: filters }),
  getL0Doc: (sha256) => api.get(`/ingesta/l0/${sha256}`),
  getDias: (filters = {}) => api.get('/ingesta/l1/dias', { params: filters }),
  getSumarioItems: (filters = {}) => api.get('/ingesta/l1/sumario-items', { params: filters }),
  getSumarioItem: (identificador) => api.get(`/ingesta/l1/sumario-items/${identificador}`),
  getTejuEdictos: (filters = {}) => api.get('/ingesta/l1/teju-edictos', { params: filters }),
  getTejuEdicto: (idBoe) => api.get(`/ingesta/l1/teju-edictos/${idBoe}`),
  getL2Extracciones: (filters = {}) => api.get('/ingesta/l2/extracciones', { params: filters }),
  getL2Extraccion: (idBoe) => api.get(`/ingesta/l2/extracciones/${idBoe}`),

  // Ficha de investigación de una extracción L2: extracción + edicto de origen
  // + resumen, señales y pasos redactados por el backend (los mismos del PDF).
  getL2Ficha: (idBoe) => api.get(`/ingesta/l2/extracciones/${idBoe}/ficha`),

  // Emitir una ficha PDF compartible de la extracción. El PDF se sirve luego
  // por fichaService.pdfUrl(token), con su uso registrado y revocable.
  crearFichaL2Pdf: (idBoe) => api.post(`/ingesta/l2/extracciones/${idBoe}/ficha-pdf`),

  // Descarga el expediente completo como fichero `.civic` para importarlo en
  // civic. Va por axios y no por window.open porque la ruta exige el Bearer;
  // lleva el edicto y la ficha PDF dentro, de ahí el timeout holgado.
  exportarCivicL2: (idBoe) =>
    api.get(`/ingesta/l2/extracciones/${idBoe}/exportar-civic`, { responseType: 'blob', timeout: 180000 }),

  // Guarda las personas de la ficha corregidas a mano (causantes y demas
  // citados del edicto, con nombre, apellidos y DNI). Van a `personas`, aparte
  // de `datos`, para que un re-extraer no se las lleve; a partir de ahi son las
  // que consultan DeepNexus y TM2. Devuelve la ficha ya recargada.
  guardarPersonasL2: (idBoe, personas) =>
    api.put(`/ingesta/l2/extracciones/${idBoe}/personas`, { personas }),

  // Marca (o desmarca) como correcto un enlace de la huella publica de una
  // persona. Solo los marcados salen en el PDF que se comparte fuera.
  // Devuelve la lista completa de marcados, no la ficha entera.
  validarOsintL2: (idBoe, nombre, url, valido) =>
    api.put(`/ingesta/l2/extracciones/${idBoe}/osint-validado`, { nombre, url, valido }),

  // Relanza el cruce TM² de un edicto (recupera la ficha genealógica de los
  // cruzados con la v1 del matcher). Responde 202 y la ficha hace polling.
  recruzarTm2L2: (idBoe) => api.post(`/ingesta/l2/extracciones/${idBoe}/tm2`, {}, { timeout: 30000 }),

  // Fija cuál de los homónimos de TM² es la persona del edicto. El worker baja
  // su árbol familiar y sigue con DeepNexus; la ficha lo va viendo por sondeo.
  seleccionarHadronL2: (idBoe, nombre, hadronId) =>
    api.post(`/ingesta/l2/extracciones/${idBoe}/tm2/seleccion`, { nombre, hadron_id: hadronId }, { timeout: 30000 }),

  // Busca los posibles hermanos de una persona ya localizada en TM²: mismos dos
  // apellidos, mismo lugar de nacimiento y ±10 años. Va síncrona y devuelve la
  // ficha recargada; el margen es amplio porque la consulta la hace TM².
  buscarHermanosL2: (idBoe, nombre) =>
    api.post(`/ingesta/l2/extracciones/${idBoe}/tm2/hermanos`, { nombre }, { timeout: 200000 }),

  // Arranca la ampliación de los causantes de una extracción L2 con DeepNexus
  // (esquelas, teléfonos y huella pública). Responde 202 al instante: el
  // trabajo sigue en el worker y la ficha se sondea hasta que termina, porque
  // tres fuentes externas por causante tardan más de lo que aguanta el proxy.
  // soloFallidas: repite solo las consultas que fallaron (timeouts, sin saldo…)
  // y conserva las que ya salieron bien.
  ampliarDeepNexusL2: (idBoe, { soloFallidas = false } = {}) =>
    api.post(`/ingesta/l2/extracciones/${idBoe}/deepnexus`, soloFallidas ? { solo_fallidas: true } : {}, { timeout: 30000 }),

  // Vuelve a extraer el edicto con el modelo de IA vigente y devuelve la ficha
  // ya recargada. La extracción anterior queda en `historial` para comparar.
  // Timeout generoso: la llamada al modelo va por dentro (el backend corta a
  // los 3 min y responde 504).
  // Reproceso en lote de L2: avance y candidatos, lanzar ('no_encontrados' |
  // 'errores') y detener.
  // Vuelve a geolocalizar UN edicto (botón «Relocalizar» de la tabla L2).
  relocalizarL2: (idBoe) => api.post(`/ingesta/l2/extracciones/${idBoe}/geo`, {}, { timeout: 190000 }),
  getReprocesoLoteL2: () => api.get('/ingesta/l2/reproceso-lote'),
  reprocesarLoteL2: (modo) => api.post('/ingesta/l2/reproceso-lote', { modo }, { timeout: 30000 }),
  cancelarReprocesoLoteL2: () => api.post('/ingesta/l2/reproceso-lote/cancelar'),
  reextraerL2: (idBoe) =>
    api.post(`/ingesta/l2/extracciones/${idBoe}/reextraer`, {}, { timeout: 200000 }),

  // Estado de gestión, notas y enlaces de trabajo de UNA fila de la ingesta,
  // sea de la capa que sea: tipo es 'l0' | 'sumario' | 'teju' | 'l2' |
  // 'depositos' y clave su identificador natural (sha256, identificador,
  // id_boe, id_anuncio). Mismo contrato que la gestión de un remanente: los
  // campos que no se mandan no se tocan. Vive fuera del documento de la capa,
  // así que un reproceso no se la lleva.
  actualizarGestionIngesta: (tipo, clave, gestion) =>
    api.patch(`/ingesta/gestion/${tipo}/${encodeURIComponent(clave)}`, gestion),

  getDepositos: (filters = {}) => api.get('/ingesta/depositos', { params: filters }),
  getDeposito: (idAnuncio) => api.get(`/ingesta/depositos/${idAnuncio}`),
};

export const healthService = {
  // Verificar salud del servidor
  check: () => api.get('/health'),
};

export const versionService = {
  // Versiones de backend, worker y mongo
  get: () => api.get('/version'),
};

export const eventsService = {
  // URL del stream SSE (EventSource no envía cabeceras → token por query param).
  streamUrl: () => {
    const token = localStorage.getItem('accessToken') || '';
    return `${API_BASE}/events/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  },
  // Comandos bajo demanda.
  commandPhase: (fase) => api.post('/events/command/phase', { fase }),
  commandSubasta: (id_boe, fase) => api.post('/events/command/subasta', { id_boe, fase }),
  commandRemanentesPhase: (fase) => api.post('/events/command/remanentes-phase', { fase }),
};

export const logService = {
  // Obtener logs de scrapeo
  getScrapeo: (params = {}) => api.get('/logs/scrapeo', { params }),
  // Obtener resumen de logs
  getSummary: () => api.get('/logs/summary'),
  // Obtener analytics del dashboard de logs
  getAnalytics: (params = {}) => api.get('/logs/analytics', { params }),
};

export const configService = {
  // Obtener todas las configuraciones
  getAll: () => api.get('/config'),

  // Obtener una configuración por clave
  getByKey: (clave) => api.get(`/config/${clave}`),

  // Crear o actualizar una configuración
  upsert: (data) => api.post('/config', data),

  // Eliminar una configuración
  delete: (clave) => api.delete(`/config/${clave}`),
};

export const groupService = {
  // Obtener todos los grupos (Admin+) o el propio (Boss)
  getAll: () => api.get('/groups'),
  
  // Obtener mi grupo (Boss/User)
  getMyGroup: () => api.get('/groups/my/me'),
  
  // Actualizar mi grupo (prompt, nombre)
  updateMyGroup: (data) => api.patch('/groups/my/me', data),

  // Probar el motor de decisión con una subasta real
  testDecision: (data) => api.post('/groups/my/me/decision-test', data),
};

export const userService = {
  // Listar usuarios (con paginación y filtros)
  getAll: (params = {}) => api.get('/users', { params }),

  // Obtener un usuario por ID
  getById: (id) => api.get(`/users/${id}`),
  
  // Obtener usuarios del grupo actual (Boss/User)
  getGroupUsers: () => api.get('/users'),
  
  // Invitar usuario
  inviteUser: (data) => api.post('/users/invite', data),
  
  // Actualizar usuario
  updateUser: (id, data) => api.patch(`/users/${id}`, data),
  
  // Eliminar/Desactivar usuario
  deleteUser: (id) => api.delete(`/users/${id}`),
};

export const setupService = {
  // API externa (god-eye): claves para consultar los fallecidos geolocalizados.
  getClavesApiExterna: () => api.get('/setup/api-externa/claves'),
  crearClaveApiExterna: (nombre) => api.post('/setup/api-externa/claves', { nombre }),
  secretoClaveApiExterna: (id) => api.get(`/setup/api-externa/claves/${id}/secreto`),
  revocarClaveApiExterna: (id) => api.delete(`/setup/api-externa/claves/${id}`),
  // Geocodificador de las direcciones de TM² (Nominatim/OSM por defecto).
  getGeocoder: () => api.get('/setup/geocoder'),
  updateGeocoder: (data) => api.put('/setup/geocoder', data),
  // Obtener configuración del scraper
  getScraper: () => api.get('/setup/scraper'),

  // Actualizar configuración del scraper
  updateScraper: (data) => api.put('/setup/scraper', data),

  // Probar proxy (devuelve la IP vista a través del proxy)
  testProxy: (url) => api.post('/setup/proxy/test', { url }),

  // Lista de proxies disponibles (servicio externo configurado en el backend)
  getProxyList: () => api.get('/setup/proxy/list'),

  // Catastro configs
  getCatastro: () => api.get('/setup/catastro'),
  updateCatastro: (data) => api.put('/setup/catastro', data),
  testCatastro: (data) => api.post('/setup/catastro/test', data),
  // Pruebas de consulta REAL: no valen para saber si el token es correcto (eso
  // lo dicen los test de arriba), sino si la peticion llega a buen fin y trae
  // datos. El timeout va por encima del del backend para que sea el, y no el
  // navegador, quien decida que se ha agotado la espera.
  probarCatastro: (data) => api.post('/setup/catastro/test-consulta', data, { timeout: 70000 }),
  probarDeepNexus: (data) => api.post('/setup/deepnexus/test-consulta', data, { timeout: 70000 }),
  probarTm2: (data) => api.post('/setup/tm2/test-consulta', data, { timeout: 70000 }),

  // DeepNexus configs (teléfonos, datos ampliados y fecha de fallecimiento)
  getDeepNexus: () => api.get('/setup/deepnexus'),
  updateDeepNexus: (data) => api.put('/setup/deepnexus', data),
  testDeepNexus: (data) => api.post('/setup/deepnexus/test', data),

  // TM² (TimeMachine) configs
  getTm2: () => api.get('/setup/tm2'),
  updateTm2: (data) => api.put('/setup/tm2', data),

  // Ajustes de la ingesta TEJU (alcance de la captura del CVE).
  getIngesta: () => api.get('/setup/ingesta'),
  updateIngesta: (data) => api.put('/setup/ingesta', data),
  testTm2: (data) => api.post('/setup/tm2/test', data),

  updateScraper: (data) => api.put('/setup/scraper', data),

  // Obtener modelos de Ollama y modelo seleccionado
  getOllama: () => api.get('/setup/ollama'),

  // Actualizar proveedor, conexion, api key y modelo de IA
  updateOllamaModel: ({ model, host, provider, api_key }) => api.put('/setup/ollama', { model, host, provider, api_key }),

  // Descargar un modelo a Ollama
  pullOllamaModel: (model) => api.post('/setup/ollama/pull', { model }),

  // Prompts de fábrica de cada fase (botón «Restaurar por defecto»).
  getPromptDefaults: () => api.get('/setup/prompt/defaults'),

  // Probar un prompt de fase ('document_summary' | 'ficha') contra el modelo
  testPrompt: (tipo, prompt) => api.post('/setup/prompt/test', { tipo, prompt }),
  // Obtener certificado activo
  getCertificate: () => api.get('/setup/certificate'),

  // Establecer certificado activo local
  setActiveLocalCertificate: (filename, password) => api.put('/setup/certificate/active', { filename, password }),

  // Subir certificado
  uploadCertificate: (formData) => api.post('/setup/certificate', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),

  // Eliminar certificado
  deleteCertificate: () => api.delete('/setup/certificate'),

  // Prueba completa del certificado activo (carga, @firma, área registrada,
  // descarga de un documento). Puede tardar ~30-60s: sin timeout en el cliente.
  testCertificate: () => api.post('/setup/certificate/test'),
};

export default api;
